import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FaShirt } from "react-icons/fa6";
import {
    Container,
    Card,
    Row,
    Col,
    Badge,
    ListGroup,
    ListGroupItem,
    Image,
    Button,
    Form,
    Alert,
    Spinner,
    Modal
} from 'react-bootstrap';
import axios from 'axios';
import useGoogleMaps from '../../hooks/useGoogleMaps';
import useUserRoles from '../../hooks/useUserRoles';
import SetRosterManagerModal from './SetRosterManagerModal';

const googleAPIKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function ManagerGameDetailPage() {
    const { gameId } = useParams();
    const [gameDetails, setGameDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editData, setEditData] = useState({});
    const [locations, setLocations] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [newDate, setNewDate] = useState('');
    const [scores, setScores] = useState({});
    const [gameManagementLoading, setGameManagementLoading] = useState(false);
    const [showRosterApprovalModal, setShowRosterApprovalModal] = useState(false);
    const [pendingRosterTeam, setPendingRosterTeam] = useState(null);
    const [rosterApprovalLoading, setRosterApprovalLoading] = useState(false);
    const [rosterData, setRosterData] = useState(null);
    const [rosterLoading, setRosterLoading] = useState(false);
    const [showRosterModal, setShowRosterModal] = useState(false);
    const [selectedTeamPlayers, setSelectedTeamPlayers] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const mapRef = useRef(null);
    const google = useGoogleMaps(googleAPIKey);
    const { isCommissioner, loading: rolesLoading } = useUserRoles();

    useEffect(() => {
        axios
            .get(`/api/games/${gameId}/?include_non_attending=true`, {
                withCredentials: true,
            })
            .then((response) => {
                setGameDetails(response.data);
                setEditData({
                    scheduled_date: response.data.scheduled_date,
                    location_id: response.data.location?.id || null,
                    division_id: response.data.division?.id || null,
                    is_played: response.data.is_played
                });
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching game details:', error);
                setError('Failed to load game details.');
                setLoading(false);
            });
    }, [gameId]);

    useEffect(() => {
        if (isCommissioner) {
            // Load locations and divisions for edit form
            Promise.all([
                axios.get('/api/games/locations/', { withCredentials: true }),
                axios.get('/api/games/divisions/', { withCredentials: true })
            ]).then(([locationsRes, divisionsRes]) => {
                setLocations(locationsRes.data);
                setDivisions(divisionsRes.data);
            }).catch(error => {
                console.error('Error loading form data:', error);
            });
        }
    }, [isCommissioner]);

    useEffect(() => {
        if (!google || !gameDetails) return;

        const mapElement = mapRef.current;
        if (!mapElement) return;

        const { formatted_address: address } = gameDetails;

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
            if (status !== "OK" || !results[0]) return;

            const map = new google.maps.Map(mapElement, {
                center: results[0].geometry.location,
                zoom: 14,
                mapId: 'e7e04a9922ec37551a45eaad',
                mapTypeControl: false,
                fullscreenControl: false,
            });

            const { AdvancedMarkerElement } = google.maps.marker;
            new AdvancedMarkerElement({
                map,
                position: results[0].geometry.location,
            });
        });
        return () => {
            mapElement.innerHTML = "";
        };
    }, [google, gameDetails]);

    const handleEdit = () => {
        setShowEditModal(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await axios.patch(`/api/games/${gameId}/`, editData, {
                withCredentials: true,
            });
            setGameDetails(response.data);
            setShowEditModal(false);
            setSaving(false);
        } catch (error) {
            console.error('Error updating game:', error);
            setError('Failed to update game details.');
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setShowEditModal(false);
        // Reset edit data to original values
        setEditData({
            scheduled_date: gameDetails.scheduled_date,
            location_id: gameDetails.location?.id || null,
            division_id: gameDetails.division?.id || null,
            is_played: gameDetails.is_played
        });
    };

    const handleInputChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCancelGame = async () => {
        setGameManagementLoading(true);
        try {
            await axios.post(`/api/games/${gameId}/cancel/`, {
                reason: cancelReason
            }, { withCredentials: true });

            // Refresh game details
            const response = await axios.get(`/api/games/${gameId}/`, {
                withCredentials: true,
            });
            setGameDetails(response.data);
            setShowCancelModal(false);
            setCancelReason('');
        } catch (error) {
            console.error('Error cancelling game:', error);
            setError('Failed to cancel game.');
        } finally {
            setGameManagementLoading(false);
        }
    };

    const handleRescheduleGame = async () => {
        setGameManagementLoading(true);
        try {
            await axios.post(`/api/games/${gameId}/reschedule/`, {
                new_date: newDate
            }, { withCredentials: true });

            // Refresh game details
            const response = await axios.get(`/api/games/${gameId}/`, {
                withCredentials: true,
            });
            setGameDetails(response.data);
            setShowRescheduleModal(false);
            setNewDate('');
        } catch (error) {
            console.error('Error rescheduling game:', error);
            setError('Failed to reschedule game.');
        } finally {
            setGameManagementLoading(false);
        }
    };

    const handleCompleteGame = async () => {
        setGameManagementLoading(true);
        try {
            // Find the ParticipantManager records for the teams
            const homeParticipant = gameDetails.participants?.find(p => p.is_home && p.content_type === 'team');
            const awayParticipant = gameDetails.participants?.find(p => p.is_away && p.content_type === 'team');

            const scoresData = [];

            if (isByeGame) {
                // For bye games, just mark the home team as winner with no score
                if (homeParticipant) {
                    scoresData.push({
                        participant_id: homeParticipant.object_id,
                        score: 0,
                        is_winner: true
                    });
                }
            } else {
                // Add home team score if provided
                if (homeParticipant && scores[homeParticipant.object_id]) {
                    const homeScore = parseInt(scores[homeParticipant.object_id]);
                    const awayScore = awayParticipant ? parseInt(scores[awayParticipant.object_id] || 0) : 0;

                    scoresData.push({
                        participant_id: homeParticipant.object_id, // Use team object_id (team ID)
                        score: homeScore,
                        is_winner: homeScore > awayScore
                    });
                }

                // Add away team score if provided
                if (awayParticipant && scores[awayParticipant.object_id]) {
                    const awayScore = parseInt(scores[awayParticipant.object_id]);
                    const homeScore = homeParticipant ? parseInt(scores[homeParticipant.object_id] || 0) : 0;

                    scoresData.push({
                        participant_id: awayParticipant.object_id, // Use team object_id (team ID)
                        score: awayScore,
                        is_winner: awayScore > homeScore
                    });
                }
            }

            await axios.post(`/api/games/${gameId}/complete/`, {
                scores: scoresData
            }, { withCredentials: true });

            // Refresh game details
            const response = await axios.get(`/api/games/${gameId}/`, {
                withCredentials: true,
            });
            setGameDetails(response.data);
            setShowCompleteModal(false);
            setScores({});
        } catch (error) {
            console.error('Error completing game:', error);
            setError('Failed to complete game.');
        } finally {
            setGameManagementLoading(false);
        }
    };

    const handleScoreChange = (participantId, score) => {
        setScores(prev => ({
            ...prev,
            [participantId]: score === '' ? '' : score
        }));
    };

    const openCompleteModal = () => {
        // Initialize scores with current values if game is already completed
        if (gameDetails.status === 'completed') {
            const initialScores = {};
            gameDetails.participants?.forEach(participant => {
                if (participant.content_type === 'team' && participant.score !== null) {
                    initialScores[participant.object_id] = participant.score.toString();
                }
            });
            setScores(initialScores);
        }
        setShowCompleteModal(true);
    };

    const handleResetToScheduled = async () => {
        setGameManagementLoading(true);
        try {
            await axios.post(`/api/games/${gameId}/reset_to_scheduled/`, {}, { withCredentials: true });

            // Refresh game details
            const response = await axios.get(`/api/games/${gameId}/`, {
                withCredentials: true,
            });
            setGameDetails(response.data);
            setShowCompleteModal(false);
            setScores({});
        } catch (error) {
            console.error('Error resetting game to scheduled:', error);
            setError('Failed to reset game to scheduled.');
        } finally {
            setGameManagementLoading(false);
        }
    };

    const openRosterApprovalModal = async (team) => {
        setPendingRosterTeam(team);
        setShowRosterApprovalModal(true);
        setRosterLoading(true);

        try {
            // Fetch the roster data for this team
            const response = await axios.get(
                `/api/games/${gameId}/teams/${team.object_id}/roster/`,
                { withCredentials: true }
            );
            console.log('Roster data:', response.data);
            console.log('Ordered player IDs:', response.data.ordered_player_ids_read);
            console.log('Team players from game data:', team.team_players);
            setRosterData(response.data);
        } catch (error) {
            console.error('Error fetching roster data:', error);
            setRosterData(null);
        } finally {
            setRosterLoading(false);
        }
    };

    const handleRosterApproval = async () => {
        if (!pendingRosterTeam) return;

        setRosterApprovalLoading(true);
        try {
            await axios.post(`/api/games/${gameId}/teams/${pendingRosterTeam.object_id}/roster/approve/`, {}, {
                withCredentials: true,
            });

            // Refresh game details
            const response = await axios.get(`/api/games/${gameId}/`, {
                withCredentials: true,
            });
            setGameDetails(response.data);
            setShowRosterApprovalModal(false);
            setPendingRosterTeam(null);
            setRosterData(null);
        } catch (error) {
            console.error('Error approving roster:', error);
            setError('Failed to approve roster.');
        } finally {
            setRosterApprovalLoading(false);
        }
    };

    // Handler for toggling player attendance (Commissioner only)
    const handlePlayerAttendanceToggle = async (player, teamId, currentAttending) => {
        if (!isCommissioner) return;
        try {
            const response = await axios.patch(
                `/api/games/${gameId}/players/${player.id}/attending/`,
                { attending: !currentAttending },
                { withCredentials: true }
            );
            if (response.status === 200) {
                // Update local state
                setGameDetails((prevDetails) => {
                    if (!prevDetails) return prevDetails;
                    return {
                        ...prevDetails,
                        participants: prevDetails.participants.map((participant) => {
                            if (participant.content_type === 'team' && participant.object_id === teamId) {
                                return {
                                    ...participant,
                                    team_players: participant.team_players.map((p) =>
                                        p.id === player.id ? { ...p, attending: !currentAttending } : p
                                    ),
                                };
                            }
                            return participant;
                        }),
                    };
                });
            }
        } catch (error) {
            console.error('Error updating player attendance:', error);
        }
    };

    const handleOpenRosterModal = (teamPlayers, teamId) => {
        setSelectedTeamPlayers(teamPlayers);
        setSelectedTeamId(teamId);
        setShowRosterModal(true);
    };

    const handleSaveRoster = async (rosterOrder) => {
        if (!selectedTeamId || !gameId) {
            console.error('Missing teamId or gameId for roster save');
            return;
        }

        try {
            const response = await axios.post(
                `/api/games/${gameId}/teams/${selectedTeamId}/roster/`,
                {
                    ordered_player_ids: rosterOrder.map(player => player.id)
                },
                {
                    withCredentials: true,
                }
            );

            console.log('Roster saved successfully:', response.data);

            // Close the modal
            setShowRosterModal(false);

            // Refresh the game details to show the updated roster order
            const gameResponse = await axios.get(`/api/games/${gameId}/?include_non_attending=true`, {
                withCredentials: true,
            });
            setGameDetails(gameResponse.data);

        } catch (error) {
            console.error('Error saving roster:', error);
        }
    };

    if (loading || rolesLoading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    const homeParticipant = gameDetails.participants?.find((p) => p.is_home && p.content_type === 'team') || {};
    const awayParticipant = gameDetails.participants?.find((p) => p.is_away && p.content_type === 'team') || {};

    const homePlayers = homeParticipant.team_players || [];
    const awayPlayers = awayParticipant.team_players || [];

    // Check if this is a bye game (no away participant)
    const isByeGame = !awayParticipant.participant_name || gameDetails.game_type === 'bye';

    // Split players into attending and not attending
    const homeAttending = homePlayers.filter(p => p.attending);
    const homeNotAttending = homePlayers.filter(p => !p.attending);
    const awayAttending = awayPlayers.filter(p => p.attending);
    const awayNotAttending = awayPlayers.filter(p => !p.attending);

    return (
        <Container fluid>
            {/* Game Banner Card */}
            <Card
                className="mb-3 border-0"
                style={{
                    backgroundImage: `url(/static/images/kb_static/lemont_rec_league_banner.png)`,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    height: '250px',
                    width: '100%',
                }}
            >
                <Card.Body className="d-flex align-items-end">
                    <Row className="w-100 justify-content-between">
                        <Col lg={2} className="ms-auto">
                            <div className="bg-light text-center rounded overflow-hidden p-1 d-inline-block">
                                <div className="bg-primary p-2 text-white rounded-top small">{gameDetails.scheduled_day_of_week}</div>
                                <h5 className="mb-0 py-2">{gameDetails.scheduled_date_only}</h5>
                                <p className="small mb-0">{gameDetails.scheduled_time_only}</p>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={handleCancel} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Game Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date & Time</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={editData.scheduled_date ? editData.scheduled_date.slice(0, 16) : ''}
                                        onChange={(e) => handleInputChange('scheduled_date', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Game Status</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="is-played-switch"
                                        label="Game has been played"
                                        checked={editData.is_played}
                                        onChange={(e) => handleInputChange('is_played', e.target.checked)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Location</Form.Label>
                                    <Form.Select
                                        value={editData.location_id || ''}
                                        onChange={(e) => handleInputChange('location_id', e.target.value ? parseInt(e.target.value) : null)}
                                    >
                                        <option value="">Select Location</option>
                                        {locations.map(location => (
                                            <option key={location.id} value={location.id}>
                                                {location.name} - {location.street_1}, {location.city}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Division</Form.Label>
                                    <Form.Select
                                        value={editData.division_id || ''}
                                        onChange={(e) => handleInputChange('division_id', e.target.value ? parseInt(e.target.value) : null)}
                                    >
                                        <option value="">Select Division</option>
                                        {divisions.map(division => (
                                            <option key={division.id} value={division.id}>
                                                {division.name} ({division.league_name})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Cancel Game Modal */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Cancel Game</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Reason for Cancellation (Optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Enter reason for cancellation..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowCancelModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleCancelGame}
                        disabled={gameManagementLoading}
                    >
                        {gameManagementLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Cancelling...
                            </>
                        ) : (
                            'Cancel Game'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reschedule Game Modal */}
            <Modal show={showRescheduleModal} onHide={() => setShowRescheduleModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Reschedule Game</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>New Date & Time</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={newDate}
                                onChange={(e) => setNewDate(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowRescheduleModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleRescheduleGame}
                        disabled={gameManagementLoading || !newDate}
                    >
                        {gameManagementLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Rescheduling...
                            </>
                        ) : (
                            'Reschedule Game'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Complete Game Modal */}
            <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {gameDetails.status === 'completed' ? 'Update Game' : 'Complete Game'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {isByeGame ? (
                            <div className="text-center">
                                <Alert variant="info">
                                    <i className="bi bi-calendar-x me-2"></i>
                                    <strong>Bye Week Game</strong>
                                    <br />
                                    This is a bye week for {homeParticipant.participant_name}. No scores need to be entered.
                                </Alert>
                            </div>
                        ) : (
                            <>
                                <Row>
                                    <Col md={6}>
                                        <h6>Home Team: {homeParticipant.participant_name}</h6>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Score</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={scores[homeParticipant.object_id] !== undefined ? scores[homeParticipant.object_id] : (homeParticipant.score || '')}
                                                onChange={(e) => handleScoreChange(homeParticipant.object_id, e.target.value)}
                                                placeholder="Enter score"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <h6>Away Team: {awayParticipant.participant_name}</h6>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Score</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={scores[awayParticipant.object_id] !== undefined ? scores[awayParticipant.object_id] : (awayParticipant.score || '')}
                                                onChange={(e) => handleScoreChange(awayParticipant.object_id, e.target.value)}
                                                placeholder="Enter score"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Alert variant="info">
                                    <i className="bi bi-info-circle me-2"></i>
                                    The team with the higher score will automatically be marked as the winner.
                                </Alert>
                            </>
                        )}

                        {/* Additional options for completed, cancelled, and rescheduled games */}
                        {(gameDetails.status === 'completed' || gameDetails.status === 'cancelled' || gameDetails.status === 'rescheduled') && (
                            <>
                                <hr />
                                <h6>Additional Actions:</h6>
                                <Row className="g-2">
                                    <Col xs={12}>
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            className="w-100"
                                            onClick={handleResetToScheduled}
                                        >
                                            <i className="bi bi-arrow-clockwise me-1"></i>
                                            Reset Game
                                        </Button>
                                    </Col>
                                </Row>
                                <Alert variant="warning" className="mt-2">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    <strong>Warning:</strong> This will clear all scores and reset the game to scheduled status.
                                </Alert>
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowCompleteModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleCompleteGame}
                        disabled={gameManagementLoading || (!isByeGame && Object.keys(scores).length < 2)}
                    >
                        {gameManagementLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {gameDetails.status === 'completed' ? 'Updating...' : 'Completing...'}
                            </>
                        ) : (
                            gameDetails.status === 'completed' ? 'Update Scores' : (isByeGame ? 'Complete Bye Week' : 'Complete Game')
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Roster Approval Modal */}
            <Modal show={showRosterApprovalModal} onHide={() => setShowRosterApprovalModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Approve Roster</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to approve the roster for{' '}
                        <strong>{pendingRosterTeam?.participant_name}</strong>?
                    </p>

                    {rosterLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading roster...</span>
                            </Spinner>
                            <p className="mt-2">Loading roster details...</p>
                        </div>
                    ) : rosterData ? (
                        <div>
                            <h6>Roster Details:</h6>

                            {/* Batting Order */}
                            <div className="mb-3">
                                <h6 className="text-primary">Batting Order:</h6>
                                <div className="row">
                                    {rosterData?.ordered_player_ids_read?.map((playerId, index) => {
                                        // Find the player in the team's player list from game data
                                        const player = pendingRosterTeam?.team_players?.find(p => p.id === playerId);
                                        return player ? (
                                            <div key={`${playerId}-${index}`} className="col-md-6 mb-2">
                                                <div className="d-flex align-items-center p-2 border rounded">
                                                    <div className="me-3">
                                                        <Badge bg="primary">{index + 1}</Badge>
                                                    </div>
                                                    <Image
                                                        src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                        alt={`${player.first_name} ${player.last_name}`}
                                                        roundedCircle
                                                        style={{ width: '30px', height: '30px' }}
                                                        className="me-2"
                                                    />
                                                    <div>
                                                        <div className="fw-bold">{player.first_name} {player.last_name}</div>
                                                        <div className="d-flex align-items-center gap-1">
                                                            <Badge
                                                                bg={player.gender === 'M' ? 'dark' : 'danger'}
                                                                className="small"
                                                                style={{ fontSize: '0.7rem' }}
                                                            >
                                                                {player.gender === 'M' ? 'M' : 'W'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div key={`${playerId}-${index}`} className="col-md-6 mb-2">
                                                <div className="d-flex align-items-center p-2 border rounded border-warning">
                                                    <div className="me-3">
                                                        <Badge bg="primary">{index + 1}</Badge>
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold text-warning">Player ID: {playerId}</div>
                                                        <small className="text-muted">Player not found in team roster</small>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Validation Errors */}
                            {rosterData.validation_errors && (
                                <div className="mb-3">
                                    <h6 className="text-warning">Validation Warnings:</h6>
                                    <Alert variant="warning">
                                        <ul className="mb-0">
                                            {Array.isArray(rosterData.validation_errors) ? (
                                                rosterData.validation_errors.map((error, index) => (
                                                    <li key={index}>{error}</li>
                                                ))
                                            ) : (
                                                <li>{rosterData.validation_errors}</li>
                                            )}
                                        </ul>
                                    </Alert>
                                </div>
                            )}

                            {/* Roster Status */}
                            <div className="mb-3">
                                <h6 className="text-info">Roster Status:</h6>
                                <div className="d-flex gap-2">
                                    <Badge bg={rosterData.is_final ? "success" : "secondary"}>
                                        {rosterData.is_final ? "Final" : "Draft"}
                                    </Badge>
                                    <Badge bg="warning" text="dark">
                                        Pending Approval
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Alert variant="danger">
                            Failed to load roster details. Please try again.
                        </Alert>
                    )}

                    <Alert variant="info">
                        <i className="bi bi-info-circle me-2"></i>
                        This will approve the roster and allow the team to use it for this game.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowRosterApprovalModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleRosterApproval}
                        disabled={rosterApprovalLoading || rosterLoading || !rosterData}
                    >
                        {rosterApprovalLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Approving...
                            </>
                        ) : (
                            'Approve Roster'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Teams VS Card */}
            <Card className="mb-3">
                <Card.Body>
                    <Row className="g-4">
                        <Col xs={isByeGame ? 12 : 5} className="text-center">
                            <h2>{homeParticipant.participant_name}</h2>
                            <div className="mb-3">
                                <Image
                                    src={homeParticipant.participant_logo}
                                    alt={`${homeParticipant.participant_name} logo`}
                                    roundedCircle
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: "white",
                                        border: '3px solid white'
                                    }}
                                />
                            </div>
                            <h5>Home</h5>
                            {homeParticipant.score !== null && (
                                <div className="mt-2">
                                    <Badge
                                        bg={homeParticipant.is_winner ? "success" : "secondary"}
                                        className="fs-4 px-3 py-2"
                                    >
                                        {homeParticipant.score}
                                    </Badge>
                                    {homeParticipant.is_winner && (
                                        <div className="mt-1">
                                            <i className="bi bi-trophy-fill text-warning"></i>
                                            <span className="ms-1 small">Winner</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {isByeGame && (
                                <div className="mt-3">
                                    <Badge bg="secondary" className="fs-5 px-4 py-2">
                                        <i className="bi bi-calendar-x me-2"></i>
                                        BYE WEEK
                                    </Badge>
                                </div>
                            )}
                        </Col>
                        {!isByeGame && (
                            <>
                                <Col xs={2} className="d-flex align-items-center justify-content-center">
                                    <h2>VS.</h2>
                                </Col>
                                <Col xs={5} className="text-center">
                                    <h2>{awayParticipant.participant_name}</h2>
                                    <div className="mb-3">
                                        <Image
                                            src={awayParticipant.participant_logo}
                                            alt={`${awayParticipant.participant_name} logo`}
                                            roundedCircle
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                backgroundColor: "white",
                                                border: '3px solid white'
                                            }}
                                        />
                                    </div>
                                    <h5>Away</h5>
                                    {awayParticipant.score !== null && (
                                        <div className="mt-2">
                                            <Badge
                                                bg={awayParticipant.is_winner ? "success" : "secondary"}
                                                className="fs-4 px-3 py-2"
                                            >
                                                {awayParticipant.score}
                                            </Badge>
                                            {awayParticipant.is_winner && (
                                                <div className="mt-1">
                                                    <i className="bi bi-trophy-fill text-warning"></i>
                                                    <span className="ms-1 small">Winner</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Col>
                            </>
                        )}
                    </Row>

                    {/* Game Management Buttons for Commissioners */}
                    {isCommissioner && (
                        <Row className="mt-4">
                            <Col className="text-center">
                                <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                                    {(gameDetails.status === 'scheduled' || gameDetails.status === 'rescheduled') && (
                                        <>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="flex-fill flex-sm-grow-0"
                                                onClick={() => setShowCancelModal(true)}
                                            >
                                                <i className="bi bi-x-circle me-1"></i>
                                                <span className="d-none d-sm-inline">Cancel Game</span>
                                                <span className="d-sm-none">Cancel</span>
                                            </Button>
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="flex-fill flex-sm-grow-0"
                                                onClick={() => setShowRescheduleModal(true)}
                                            >
                                                <i className="bi bi-calendar-event me-1"></i>
                                                <span className="d-none d-sm-inline">Reschedule</span>
                                                <span className="d-sm-none">Reschedule</span>
                                            </Button>
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="flex-fill flex-sm-grow-0"
                                                onClick={openCompleteModal}
                                            >
                                                <i className="bi bi-check-circle me-1"></i>
                                                <span className="d-none d-sm-inline">Complete Game</span>
                                                <span className="d-sm-none">Complete</span>
                                            </Button>
                                        </>
                                    )}
                                    {gameDetails.status === 'cancelled' && (
                                        <>
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="flex-fill flex-sm-grow-0"
                                                onClick={() => setShowRescheduleModal(true)}
                                            >
                                                <i className="bi bi-calendar-event me-1"></i>
                                                <span className="d-none d-sm-inline">Reschedule Game</span>
                                                <span className="d-sm-none">Reschedule</span>
                                            </Button>
                                            <Button
                                                variant="outline-info"
                                                size="sm"
                                                className="flex-fill flex-sm-grow-0"
                                                onClick={handleResetToScheduled}
                                            >
                                                <i className="bi bi-arrow-clockwise me-1"></i>
                                                <span className="d-none d-sm-inline">Reset to Scheduled</span>
                                                <span className="d-sm-none">Reset</span>
                                            </Button>
                                        </>
                                    )}
                                    {gameDetails.status === 'completed' && (
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            className="flex-fill flex-sm-grow-0"
                                            onClick={openCompleteModal}
                                        >
                                            <i className="bi bi-pencil-square me-1"></i>
                                            <span className="d-none d-sm-inline">Update Game</span>
                                            <span className="d-sm-none">Update</span>
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Game Details and Map Card */}
            <Card className="mb-2">
                <Card.Body>
                    <Row>
                        <Col md={6} className="d-flex flex-column justify-content-center">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="mb-0 text-center">Game Details:</h5>
                                {isCommissioner && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="btn-sm"
                                        onClick={handleEdit}
                                    >
                                        <i className="bi bi-pencil me-1"></i>
                                        <span className="d-none d-sm-inline">Edit Details</span>
                                        <span className="d-sm-inline">Edit</span>
                                    </Button>
                                )}
                            </div>
                            <ListGroup variant="flush">
                                <ListGroupItem className="d-flex justify-content-between">
                                    <strong>Date:</strong>
                                    <span>{gameDetails.scheduled_date_only}</span>
                                </ListGroupItem>
                                <ListGroupItem className="d-flex justify-content-between">
                                    <strong>Time:</strong>
                                    <span>{gameDetails.scheduled_time_only}</span>
                                </ListGroupItem>
                                <ListGroupItem className="d-flex justify-content-between">
                                    <strong>Address:</strong>
                                    <div className="text-end">
                                        <div>{gameDetails.location.street_1} {gameDetails.location.street_2}</div>
                                        <div>{gameDetails.location.city}, {gameDetails.location.state}</div>
                                    </div>
                                </ListGroupItem>
                            </ListGroup>
                        </Col>
                        <Col md={6}>
                            <div
                                ref={mapRef}
                                style={{ width: "100%", height: 230, borderRadius: 4 }}
                                className="grayscale"
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Teams Details Row */}
            <Row className="g-4">
                {/* Home Team Card */}
                <Col xs={12} lg={isByeGame ? 12 : 6}>
                    <Card>
                        <Card.Header>
                            <Row className="text-center align-items-center justify-content-between">
                                <Col xs={6}>
                                    <h5 className="card-title">Team Captain</h5>
                                    <p className="small mb-0">{homeParticipant.team_captain || 'No Captain'}</p>
                                </Col>
                                <Col xs={6}>
                                    <h5 className="card-title">Team Color</h5>
                                    <p className="small mb-0">
                                        <FaShirt style={{ fontSize: '20px', color: homeParticipant.team_color?.value || '#6c757d' }} />
                                        {' '}{homeParticipant.team_color?.name || 'Unknown'}
                                    </p>
                                </Col>
                            </Row>
                            {homeParticipant.roster_pending_approval && (
                                <Row className="mt-2">
                                    <Col className="text-center">
                                        <Badge bg="warning" text="dark">
                                            <i className="bi bi-clock me-1"></i>
                                            Roster Pending Approval
                                        </Badge>
                                    </Col>
                                </Row>
                            )}
                        </Card.Header>

                        {/* Team Stats */}
                        <Card.Body>
                            <Row className="text-center mb-3">
                                <Col xs={4}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <i className="bi bi-trophy fs-4 me-2"></i>
                                        <div>
                                            <h5 className="mb-0">{homeParticipant.team_wins || 0}</h5>
                                            <p className="mb-0 small">Wins</p>
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={4}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <i className="bi bi-trophy-fill fs-4 me-2"></i>
                                        <div>
                                            <h5 className="mb-0">{homeParticipant.team_losses || 0}</h5>
                                            <p className="mb-0 small">Losses</p>
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={4}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <i className="bi bi-trophy-fill fs-4 me-2"></i>
                                        <div>
                                            <h5 className="mb-0">{homeParticipant.team_draws || 0}</h5>
                                            <p className="mb-0 small">Draws</p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>

                        {/* Players Section */}
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">Players</h5>
                                <div className="d-flex align-items-center gap-2">
                                    {/* Roster Status Badge */}
                                    <button
                                        className={`btn btn-sm ${homeParticipant.roster_is_final ? 'btn-success' : homeParticipant.roster_pending_approval ? 'btn-warning' : 'btn-outline-secondary'}`}
                                        onClick={() => {
                                            if (isCommissioner) {
                                                // Open roster modal for editing
                                                handleOpenRosterModal(homePlayers, homeParticipant.object_id);
                                            }
                                        }}
                                        disabled={!isCommissioner}
                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                    >
                                        <i className={`bi ${homeParticipant.roster_is_final ? 'bi-lock-fill' : homeParticipant.roster_pending_approval ? 'bi-clock' : 'bi-pencil'} me-1`}></i>
                                        {homeParticipant.roster_is_final ? 'Final' : homeParticipant.roster_pending_approval ? 'Pending' : 'Draft'}
                                        <span className="ms-1 badge bg-light text-dark">
                                            {homePlayers.length}
                                        </span>
                                    </button>
                                    {isCommissioner && homeParticipant.roster_pending_approval && (
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            onClick={() => openRosterApprovalModal(homeParticipant)}
                                        >
                                            <i className="bi bi-check-circle me-1"></i>
                                            Approve
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {/* Attending Section */}
                            <h6 className="mb-3">Attending</h6>
                            <Row>
                                {homeAttending.map((player) => (
                                    <Col key={player.id} xs={12} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            <Image
                                                src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                alt={`${player.first_name} ${player.last_name}`}
                                                roundedCircle
                                                style={{ width: '40px', height: '40px' }}
                                                className="me-3"
                                            />
                                            <div className="flex-grow-1">
                                                <h6 className="mb-0">{player.first_name} {player.last_name}</h6>
                                                <p className="small mb-0">
                                                    <i className="bi bi-geo-alt pe-1"></i>Lemont
                                                </p>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                {player.score !== null && (
                                                    <Badge
                                                        bg={player.is_winner ? "success" : "secondary"}
                                                        className="fs-6"
                                                    >
                                                        {player.score}
                                                    </Badge>
                                                )}
                                                <button
                                                    className={`btn btn-sm ${player.attending ? 'btn-success' : 'btn-outline-success'}${!isCommissioner ? ' disabled' : ''}`}
                                                    onClick={() => handlePlayerAttendanceToggle(player, homeParticipant.object_id, player.attending)}
                                                    disabled={!isCommissioner}
                                                >
                                                    <i className="fa-solid fa-thumbs-up me-1"></i> Attending
                                                </button>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                            {/* Not Attending Section */}
                            {homeNotAttending.length > 0 && (
                                <>
                                    <hr className="my-3" />
                                    <h6 className="text-muted mb-3">
                                        <i className="bi bi-x-circle me-2"></i>
                                        Not Attending ({homeNotAttending.length})
                                    </h6>
                                    <Row>
                                        {homeNotAttending.map((player) => (
                                            <Col key={player.id} xs={12} className="mb-3">
                                                <div className="d-flex align-items-center">
                                                    <Image
                                                        src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                        alt={`${player.first_name} ${player.last_name}`}
                                                        roundedCircle
                                                        style={{ width: '40px', height: '40px', opacity: 0.5 }}
                                                        className="me-3"
                                                    />
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-0 text-muted">{player.first_name} {player.last_name}</h6>
                                                        <p className="small mb-0 text-muted">
                                                            <i className="bi bi-geo-alt pe-1"></i>Lemont
                                                        </p>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <button
                                                            className={`btn btn-sm btn-outline-danger${!isCommissioner ? ' disabled' : ''}`}
                                                            onClick={() => handlePlayerAttendanceToggle(player, homeParticipant.object_id, player.attending)}
                                                            disabled={!isCommissioner}
                                                        >
                                                            <i className="fa-solid fa-times me-1"></i> Not Attending
                                                        </button>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Away Team Card - Only show for non-bye games */}
                {!isByeGame && (
                    <Col xs={12} lg={6}>
                        <Card>
                            <Card.Header>
                                <Row className="text-center align-items-center justify-content-between">
                                    <Col xs={6}>
                                        <h5 className="card-title">Team Captain</h5>
                                        <p className="small mb-0">{awayParticipant.team_captain || 'No Captain'}</p>
                                    </Col>
                                    <Col xs={6}>
                                        <h5 className="card-title">Team Color</h5>
                                        <p className="small mb-0">
                                            <FaShirt style={{ fontSize: '20px', color: awayParticipant.team_color?.value || '#6c757d' }} />
                                            {' '}{awayParticipant.team_color?.name || 'Unknown'}
                                        </p>
                                    </Col>
                                </Row>
                                {awayParticipant.roster_pending_approval && (
                                    <Row className="mt-2">
                                        <Col className="text-center">
                                            <Badge bg="warning" text="dark">
                                                <i className="bi bi-clock me-1"></i>
                                                Roster Pending Approval
                                            </Badge>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Header>

                            {/* Team Stats */}
                            <Card.Body>
                                <Row className="text-center mb-3">
                                    <Col xs={4}>
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="bi bi-trophy fs-4 me-2"></i>
                                            <div>
                                                <h5 className="mb-0">{awayParticipant.team_wins || 0}</h5>
                                                <p className="mb-0 small">Wins</p>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={4}>
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="bi bi-trophy-fill fs-4 me-2"></i>
                                            <div>
                                                <h5 className="mb-0">{awayParticipant.team_losses || 0}</h5>
                                                <p className="mb-0 small">Losses</p>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={4}>
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="bi bi-trophy-fill fs-4 me-2"></i>
                                            <div>
                                                <h5 className="mb-0">{awayParticipant.team_draws || 0}</h5>
                                                <p className="mb-0 small">Draws</p>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>

                            {/* Players Section */}
                            <Card.Header>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">Players</h5>
                                    <div className="d-flex align-items-center gap-2">
                                        {/* Roster Status Badge */}
                                        <button
                                            className={`btn btn-sm ${awayParticipant.roster_is_final ? 'btn-success' : awayParticipant.roster_pending_approval ? 'btn-warning' : 'btn-outline-secondary'}`}
                                            onClick={() => {
                                                if (isCommissioner) {
                                                    // Open roster modal for editing
                                                    handleOpenRosterModal(awayPlayers, awayParticipant.object_id);
                                                }
                                            }}
                                            disabled={!isCommissioner}
                                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                        >
                                            <i className={`bi ${awayParticipant.roster_is_final ? 'bi-lock-fill' : awayParticipant.roster_pending_approval ? 'bi-clock' : 'bi-pencil'} me-1`}></i>
                                            {awayParticipant.roster_is_final ? 'Final' : awayParticipant.roster_pending_approval ? 'Pending' : 'Draft'}
                                            <span className="ms-1 badge bg-light text-dark">
                                                {awayPlayers.length}
                                            </span>
                                        </button>
                                        {isCommissioner && awayParticipant.roster_pending_approval && (
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                onClick={() => openRosterApprovalModal(awayParticipant)}
                                            >
                                                <i className="bi bi-check-circle me-1"></i>
                                                Approve
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {/* Attending Section */}
                                <h6 className="mb-3">Attending</h6>
                                <Row>
                                    {awayAttending.map((player) => (
                                        <Col key={player.id} xs={12} className="mb-3">
                                            <div className="d-flex align-items-center">
                                                <Image
                                                    src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                    alt={`${player.first_name} ${player.last_name}`}
                                                    roundedCircle
                                                    style={{ width: '40px', height: '40px' }}
                                                    className="me-3"
                                                />
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-0">{player.first_name} {player.last_name}</h6>
                                                    <p className="small mb-0">
                                                        <i className="bi bi-geo-alt pe-1"></i>Lemont
                                                    </p>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    {player.score !== null && (
                                                        <Badge
                                                            bg={player.is_winner ? "success" : "secondary"}
                                                            className="fs-6"
                                                        >
                                                            {player.score}
                                                        </Badge>
                                                    )}
                                                    <button
                                                        className={`btn btn-sm ${player.attending ? 'btn-success' : 'btn-outline-success'}${!isCommissioner ? ' disabled' : ''}`}
                                                        onClick={() => handlePlayerAttendanceToggle(player, awayParticipant.object_id, player.attending)}
                                                        disabled={!isCommissioner}
                                                    >
                                                        <i className="fa-solid fa-thumbs-up me-1"></i> Attending
                                                    </button>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                                {/* Not Attending Section */}
                                {awayNotAttending.length > 0 && (
                                    <>
                                        <hr className="my-3" />
                                        <h6 className="text-muted mb-3">
                                            <i className="bi bi-x-circle me-2"></i>
                                            Not Attending ({awayNotAttending.length})
                                        </h6>
                                        <Row>
                                            {awayNotAttending.map((player) => (
                                                <Col key={player.id} xs={12} className="mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <Image
                                                            src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                            alt={`${player.first_name} ${player.last_name}`}
                                                            roundedCircle
                                                            style={{ width: '40px', height: '40px', opacity: 0.5 }}
                                                            className="me-3"
                                                        />
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-0 text-muted">{player.first_name} {player.last_name}</h6>
                                                            <p className="small mb-0 text-muted">
                                                                <i className="bi bi-geo-alt pe-1"></i>Lemont
                                                            </p>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <button
                                                                className={`btn btn-sm btn-outline-danger${!isCommissioner ? ' disabled' : ''}`}
                                                                onClick={() => handlePlayerAttendanceToggle(player, awayParticipant.object_id, player.attending)}
                                                                disabled={!isCommissioner}
                                                            >
                                                                <i className="fa-solid fa-times me-1"></i> Not Attending
                                                            </button>
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Set Roster Modal */}
            <SetRosterManagerModal
                show={showRosterModal}
                onHide={() => setShowRosterModal(false)}
                teamPlayers={selectedTeamPlayers}
                onSave={handleSaveRoster}
                gameId={gameId}
                teamId={selectedTeamId}
                isCommissioner={isCommissioner}
            />
        </Container>
    );
}

export default ManagerGameDetailPage;
